import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ApiError } from '~/api/errors';
import { queryKeys } from '~/api/queryKeys';
import { useAuthStore } from '~/store/auth.store';
import { extractApiError } from '~/utils/extract-api-error';

import {
  getWorkflowFailurePoints,
  retryWorkflowFromStep,
} from '../services/catalog-operations.service';
import type { WorkflowRetryMode } from '../types/catalog-operations.types';

/** Discovers Temporal failure points for the batch's workflow. `enabled` gates
 *  the call to failed batches only — avoids a wasted request on healthy ones. */
export function useWorkflowFailurePoints(workflowId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.catalogOperations.failurePoints(workflowId),
    queryFn: () => getWorkflowFailurePoints(workflowId),
    enabled: enabled && !!workflowId,
  });
}

/** Retries the batch's workflow from a chosen failure point in the operator's
 *  chosen `mode` (`RESET` — Temporal rewind — or, for a scheduled pipeline's
 *  failed `STAGE:ENRICHMENT` point only, `RESUME_REMAINING` — a fresh run of
 *  the same `workflowId` that enriches just the missing products). On
 *  success, refetches batch detail so the pipeline stages reflect the new
 *  run. A `400` means the chosen point/mode is no longer valid (the page is
 *  stale) — refetch failure-points so the dialog re-renders with the
 *  server's current `retryModes` instead of a plain error toast.
 *
 *  `jobId` is the job uuid used to key the batch-detail query; `workflowId`
 *  is `BatchDetail.batchId`, the identifier the retry endpoint expects. */
export function useRetryWorkflow(jobId: string, workflowId: string) {
  const { t } = useTranslation('catalog-operations');
  const queryClient = useQueryClient();
  const userEmail = useAuthStore((s) => s.user?.email);

  return useMutation({
    mutationFn: ({
      runId,
      eventId,
      mode,
    }: {
      runId: string;
      eventId: number;
      mode: WorkflowRetryMode;
    }) =>
      retryWorkflowFromStep(
        workflowId,
        runId,
        eventId,
        mode === 'RESUME_REMAINING'
          ? `Continue remaining products — triggered from catalog-operations UI by ${userEmail ?? 'unknown user'}`
          : `Re-run enrichment from start — triggered from catalog-operations UI by ${userEmail ?? 'unknown user'}`,
        mode,
      ),
    onSuccess: async (data) => {
      toast.success(
        data.mode === 'RESUME_REMAINING'
          ? t('retry.successResume', { runId: data.newRunId })
          : t('retry.success', { runId: data.newRunId }),
      );
      await queryClient.invalidateQueries({
        queryKey: queryKeys.catalogOperations.batchDetail(jobId),
      });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.statusCode === 400) {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.catalogOperations.failurePoints(workflowId),
        });
        toast.warning(extractApiError(err, t('retry.stale')));
        return;
      }
      toast.error(extractApiError(err, t('retry.error')));
    },
  });
}
